import React from "react";
import { Icon } from "@iconify/react";
import moment from "moment";
import "../caseDetail.css";

/**
 * Why a case was sent back.
 *
 * The API stores every one of these in the same `reject_reason` column
 * and sets status 3 for all of them, so the record alone cannot say
 * which of the three happened:
 *
 *   - a doctor rejected the submitted plan
 *   - the technician who took it abandoned it
 *   - a technician put it on hold: the data does not meet requirements
 *
 * On hold is identifiable (refine_id === id). For the other two the
 * backend writes a discussion entry naming the actor and the action, so
 * the newest matching entry is used to label the banner and name who
 * did it. If no entry matches the wording, the banner still shows the
 * reason and just does not claim who wrote it.
 */

const MARKERS = [
  { test: /rejected case/i, label: 'Rejected by the doctor', icon: 'tabler:x', tone: 'rejected' },
  { test: /amend case/i, label: 'Abandoned by the technician', icon: 'tabler:hand-off', tone: 'modification' },
  // Matches the wording UserCaseController::cancelCase() writes.
  { test: /on hold/i, label: 'On hold: data does not meet requirements', icon: 'tabler:player-pause', tone: 'modification' },
];

const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const findAction = (discussions) => {
  if (!Array.isArray(discussions)) return null;
  for (const entry of discussions) {
    const text = stripHtml(entry.message);
    const match = MARKERS.find((m) => m.test.test(text));
    if (match) return { ...match, by: entry.name, at: entry.created_at };
  }
  return null;
};

const CaseReasonBanner = ({ caseData }) => {
  const reason = caseData && caseData.reject_reason;
  if (!reason || caseData.status !== 3) return null;

  const isOnHold = caseData.refine_id === caseData.id;
  const action = findAction(caseData.discussions);

  const heading = isOnHold
    ? 'On hold: data does not meet requirements'
    : (action ? action.label : 'Case sent back');
  const icon = isOnHold
    ? 'tabler:player-pause'
    : (action ? action.icon : 'tabler:message-report');
  const tone = isOnHold ? 'modification' : (action ? action.tone : 'rejected');

  return (
    <aside className={ `cd-reason status-${tone}` }>
      <span className="cd-reason-mark">
        <Icon icon={ icon } width={ 20 } />
      </span>

      <div className="cd-reason-body">
        <p className="cd-reason-head">
          { heading }
          { action && action.by &&
            <span className="cd-reason-by">
              by { action.by }
              { action.at && <> &middot; { moment(action.at).fromNow() }</> }
            </span> }
        </p>
        <p className="cd-reason-text">{ reason }</p>
      </div>
    </aside>
  );
};

export default CaseReasonBanner;
